import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import BlockIcon from "@material-ui/icons/Block";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/WarningRounded";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";
import SegmentedControl from "../SegmentedControl";
import {
  formatDayShort,
  formatDate,
  formatDuration,
} from "../../helpers/scheduleBlocks";

const QUICK_REASONS = ["Férias", "Feriado", "Folga", "Consulta médica", "Reunião"];
const MAX_DAYS = 90;
const today = () => moment().format("YYYY-MM-DD");

const useStyles = makeStyles((theme) => ({
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
  },
  quick: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.75) },
  quickChip: {
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.primary,
    font: "inherit",
    fontSize: 12,
    fontWeight: 500,
    height: 28,
    padding: theme.spacing(0, 1.25),
    borderRadius: 14,
    cursor: "pointer",
    "&:hover": { backgroundColor: theme.palette.action.hover },
  },
  quickChipOn: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderColor: "transparent",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  preview: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: `1px dashed ${theme.palette.divider}`,
    fontSize: 13,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 24,
    padding: theme.spacing(0, 1.25),
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    "& svg": { fontSize: 14 },
  },
  chipFull: {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.type === "dark" ? "#EF7B7B" : theme.palette.error.dark,
  },
  chipTime: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  },
  callout: {
    display: "flex",
    gap: theme.spacing(1.25),
    alignItems: "flex-start",
    borderRadius: 10,
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13,
    color: theme.palette.text.primary,
    "& svg": { fontSize: 18, marginTop: 1, flexShrink: 0 },
  },
  calloutWarn: {
    backgroundColor: alpha(theme.palette.warning.main, 0.16),
    "& svg": { color: theme.palette.warning.main },
  },
  calloutInfo: {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    "& svg": { color: theme.palette.primary.main },
  },
  footerSplit: { justifyContent: "space-between" },
  deleteButton: { color: theme.palette.error.main },
}));

const ScheduleBlockModal = ({ open, onClose, onSaved, item, onDelete }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const editing = Boolean(item);
  const isSeries = editing && item.days > 1; // edição de vários dias: datas fixas

  const [userId, setUserId] = useState("");
  const [mode, setMode] = useState("day"); // "day" | "range"
  const [date, setDate] = useState(today());
  const [dateEnd, setDateEnd] = useState(today());
  const [isFullDay, setIsFullDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("09:00");
  const [reason, setReason] = useState("");
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState(null); // null = ainda não checou

  // Preenche o formulário ao abrir (novo ou edição).
  useEffect(() => {
    if (!open) return;
    if (item) {
      setUserId(item.userId);
      setMode("day");
      setDate(item.dateStart);
      setDateEnd(item.dateEnd);
      setIsFullDay(Boolean(item.isFullDay));
      setStartTime(item.startTime || "08:00");
      setEndTime(item.endTime || "09:00");
      setReason(item.reason || "");
    } else {
      setUserId("");
      setMode("day");
      setDate(today());
      setDateEnd(today());
      setIsFullDay(true);
      setStartTime("08:00");
      setEndTime("09:00");
      setReason("");
    }
    setConflicts(null);
  }, [open, item]);

  // Só admin escolhe profissional.
  useEffect(() => {
    if (!open || !isAdmin) return;
    let cancelled = false;
    (async () => {
      setLoadingProviders(true);
      try {
        const { data } = await api.get("/users/list");
        if (!cancelled) setProviders((data || []).filter((u) => u.isServiceProvider));
      } catch (err) {
        toastError(err);
      } finally {
        if (!cancelled) setLoadingProviders(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isAdmin]);

  const effectiveEnd = mode === "range" || isSeries ? dateEnd : date;
  const days = useMemo(() => {
    const a = moment(date, "YYYY-MM-DD", true);
    const b = moment(effectiveEnd, "YYYY-MM-DD", true);
    if (!a.isValid() || !b.isValid() || b.isBefore(a)) return 0;
    return b.diff(a, "days") + 1;
  }, [date, effectiveEnd]);

  const timeInvalid = !isFullDay && (!startTime || !endTime || startTime >= endTime);

  // Avisa se já existem agendamentos no período escolhido.
  useEffect(() => {
    if (!open || !days || timeInvalid || (isAdmin && !userId)) {
      setConflicts(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/schedule-blocks/conflicts", {
          params: {
            userId: isAdmin ? userId : undefined,
            dateFrom: date,
            dateTo: effectiveEnd,
            startTime: isFullDay ? undefined : startTime,
            endTime: isFullDay ? undefined : endTime,
          },
        });
        if (!cancelled) setConflicts(data);
      } catch (err) {
        if (!cancelled) setConflicts(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, days, timeInvalid, isAdmin, userId, date, effectiveEnd, isFullDay, startTime, endTime]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const validate = () => {
    if (isAdmin && !userId) return "Selecione o profissional.";
    if (!date) return "Informe a data do bloqueio.";
    if (mode === "range" && !editing) {
      if (!days) return "A data final não pode ser anterior à inicial.";
      if (days > MAX_DAYS) return `O bloqueio pode ter no máximo ${MAX_DAYS} dias.`;
    }
    if (timeInvalid) return "O horário de início deve ser menor que o de término.";
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const common = {
        isFullDay,
        startTime: isFullDay ? undefined : startTime,
        endTime: isFullDay ? undefined : endTime,
        reason: reason.trim() || undefined,
      };
      if (editing) {
        await Promise.all(
          item.blocks.map((b) =>
            api.put(`/schedule-blocks/${b.id}`, {
              ...common,
              date: isSeries ? b.date : date,
            })
          )
        );
        toast.success("Bloqueio atualizado.");
      } else {
        await api.post("/schedule-blocks", {
          ...common,
          userId: isAdmin ? userId : undefined, // backend usa o usuário logado para não-admin
          date,
          dateEnd: mode === "range" && dateEnd !== date ? dateEnd : undefined,
        });
        toast.success(days > 1 ? "Bloqueios adicionados." : "Bloqueio adicionado.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const nDays = editing ? item.days : days;
  const subtitle = editing
    ? `${item.user?.name || ""} · ${
        isSeries
          ? `${formatDayShort(item.dateStart)} → ${formatDayShort(item.dateEnd)}`
          : formatDate(item.dateStart)
      }`
    : "Os horários bloqueados não aparecem para agendamento na página pública.";

  const submitLabel = editing
    ? "Salvar alterações"
    : nDays > 1
    ? `Bloquear ${nDays} dias`
    : "Bloquear dia";

  const dayRange =
    nDays > 1
      ? `${formatDayShort(date)} → ${formatDayShort(effectiveEnd)} · ${nDays} dias`
      : date
      ? formatDayShort(date)
      : "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="schedule-block-title"
    >
      <FormDialogHeader
        id="schedule-block-title"
        title={editing ? "Editar bloqueio" : "Novo bloqueio"}
        subtitle={subtitle}
        onClose={handleClose}
        disabled={submitting}
      />

      <DialogContent dividers className={dialogClasses.content}>
        {isAdmin && !editing && (
          <FormSection title="Profissional">
            <TextField
              select
              variant="outlined"
              size="small"
              fullWidth
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={loadingProviders}
              inputProps={{ "aria-label": "Profissional" }}
              SelectProps={{ displayEmpty: true }}
              helperText={
                !loadingProviders && providers.length === 0
                  ? "Nenhum profissional cadastrado."
                  : undefined
              }
            >
              <MenuItem value="" disabled>
                Selecione o profissional
              </MenuItem>
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>
        )}

        <FormSection title="Quando">
          {!editing && (
            <SegmentedControl
              fullWidth
              ariaLabel="Quantidade de dias"
              value={mode}
              onChange={(v) => {
                setMode(v);
                if (v === "range" && dateEnd < date) setDateEnd(date);
              }}
              options={[
                { value: "day", label: "Um dia" },
                { value: "range", label: "Vários dias" },
              ]}
            />
          )}

          {isSeries ? (
            <Typography variant="body2" className={dialogClasses.muted}>
              As datas deste bloqueio não mudam aqui. Para outro período, exclua e crie um novo.
            </Typography>
          ) : (
            <div className={mode === "range" && !editing ? classes.row2 : undefined}>
              <TextField
                label={mode === "range" && !editing ? "Início" : "Data"}
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (dateEnd < e.target.value) setDateEnd(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: editing ? undefined : today() }}
                variant="outlined"
                size="small"
                fullWidth
              />
              {mode === "range" && !editing && (
                <TextField
                  label="Fim"
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: date || today() }}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}
            </div>
          )}

          <SegmentedControl
            fullWidth
            ariaLabel="Período do dia"
            value={isFullDay ? "full" : "time"}
            onChange={(v) => setIsFullDay(v === "full")}
            options={[
              { value: "full", label: "Dia inteiro" },
              { value: "time", label: "Só um horário" },
            ]}
          />

          {!isFullDay && (
            <div className={classes.row2}>
              <TextField
                label="Das"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                variant="outlined"
                size="small"
                error={timeInvalid}
                fullWidth
              />
              <TextField
                label="Até"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                variant="outlined"
                size="small"
                error={timeInvalid}
                fullWidth
              />
            </div>
          )}
          {timeInvalid && (
            <Typography variant="caption" color="error">
              O horário de início deve ser menor que o de término.
            </Typography>
          )}
        </FormSection>

        <FormSection title="Motivo (opcional)">
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Ex.: Férias, consulta médica, feriado…"
            inputProps={{ "aria-label": "Motivo", maxLength: 120 }}
          />
          <div className={classes.quick}>
            {QUICK_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                className={`${classes.quickChip} ${reason === r ? classes.quickChipOn : ""}`}
                onClick={() => setReason(reason === r ? "" : r)}
              >
                {r}
              </button>
            ))}
          </div>
        </FormSection>

        {dayRange && !timeInvalid && (
          <div className={classes.preview}>
            {isFullDay ? (
              <span className={`${classes.chip} ${classes.chipFull}`}>
                <BlockIcon /> Dia inteiro
              </span>
            ) : (
              <span className={`${classes.chip} ${classes.chipTime}`}>
                <AccessTimeIcon /> {startTime} – {endTime}
              </span>
            )}
            <span>
              {dayRange}
              {!isFullDay && formatDuration(startTime, endTime)
                ? ` · ${formatDuration(startTime, endTime)} por dia`
                : ""}
            </span>
          </div>
        )}

        {conflicts && conflicts.count > 0 && (
          <div className={`${classes.callout} ${classes.calloutWarn}`}>
            <WarningIcon />
            <div>
              <b>
                {conflicts.count} {conflicts.count === 1 ? "agendamento" : "agendamentos"}
              </b>{" "}
              já {conflicts.count === 1 ? "marcado" : "marcados"} nesse período (
              {conflicts.appointments
                .slice(0, 3)
                .map((a) => moment(a.startAt).format("DD/MM HH:mm"))
                .join(", ")}
              {conflicts.count > 3 ? "…" : ""}). O bloqueio não cancela esses horários.
            </div>
          </div>
        )}
        {conflicts && conflicts.count === 0 && (
          <div className={`${classes.callout} ${classes.calloutInfo}`}>
            <InfoOutlinedIcon />
            <div>Nenhum agendamento nesse período.</div>
          </div>
        )}
      </DialogContent>

      <DialogActions
        className={`${dialogClasses.actions} ${editing ? classes.footerSplit : ""}`}
      >
        {editing && (
          <Button
            onClick={() => onDelete(item)}
            disabled={submitting}
            className={`${dialogClasses.textButton} ${classes.deleteButton}`}
          >
            Excluir
          </Button>
        )}
        <div>
          <Button
            onClick={handleClose}
            disabled={submitting}
            className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disableElevation
            disabled={submitting || (isAdmin && !editing && loadingProviders)}
            className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
          >
            {submitLabel}
            {submitting && (
              <CircularProgress size={24} className={dialogClasses.buttonProgress} />
            )}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleBlockModal;
